/**
 * Centralized user status derivation.
 *
 * Single source of truth used by the User Management table, the
 * user detail drawer, and any row-action component that needs to
 * decide whether to show Lock vs Unlock. Replaces the previously
 * duplicated inline checks (`user.status === 'suspended' || 'locked'`)
 * that diverged across views — e.g. the table showed `disabled`
 * users as "Active" while the drawer showed the raw "disabled"
 * string.
 *
 * Status semantics (server-defined — see backend/models/admin.py
 * UserStatus and routes/admin_users.py):
 *   - 'active'                : user can sign in
 *   - 'locked' / 'suspended'  : admin locked the account; toggle via Lock/Unlock
 *   - 'disabled'              : soft-deleted; cannot sign in
 *   - 'pending_verification'  : awaiting email verification
 *
 * Anything else (or missing/null) falls back to "Active" to match
 * historical behavior. Email-verified=false also surfaces as Pending
 * for parity with how the drawer's resend-verification gate already
 * treats it.
 */

export const USER_STATUS = Object.freeze({
  ACTIVE: 'active',
  LOCKED: 'locked',
  SUSPENDED: 'suspended',
  DISABLED: 'disabled',
  PENDING_VERIFICATION: 'pending_verification',
});

/**
 * @param {object} user - user object (typically from /api/admin/users)
 * @returns {{
 *   raw: string,
 *   label: string,
 *   badgeClass: string,   // matches existing CSS conventions (um-status-*)
 *   isLocked: boolean,    // includes 'suspended'
 *   isDisabled: boolean,
 *   isPending: boolean,
 *   isActive: boolean,
 * }}
 */
export function getUserStatus(user) {
  const rawStatus = user?.status ? user.status.toString().toLowerCase() : null;
  const raw = rawStatus || 'active';

  const isLocked = raw === USER_STATUS.LOCKED || raw === USER_STATUS.SUSPENDED;
  const isDisabled = raw === USER_STATUS.DISABLED;
  // Pending when the status field is explicitly pending_verification, OR
  // when email_verified is explicitly false and the status hasn't been
  // confirmed as 'active' yet (covers freshly-invited users whose status
  // field is missing entirely).
  const isPending =
    raw === USER_STATUS.PENDING_VERIFICATION ||
    (user?.email_verified === false && rawStatus !== USER_STATUS.ACTIVE);
  const isActive = !isLocked && !isDisabled && !isPending;

  let label;
  let badgeClass;

  if (isDisabled) {
    label = 'Disabled';
    badgeClass = 'um-status-disabled';
  } else if (isLocked) {
    label = 'Locked';
    badgeClass = 'um-status-locked';
  } else if (isPending) {
    label = 'Pending';
    badgeClass = 'um-status-pending';
  } else {
    label = 'Active';
    badgeClass = 'um-status-active';
  }

  return { raw, label, badgeClass, isLocked, isDisabled, isPending, isActive };
}
